import React from 'react';
import NotFound from '~/App/shared/components/NotFound';
import {
  CarBrandAndCategoryBySlugDocument,
  CarBrandAndCategoryBySlugQuery,
  PromoSettingByUsageTypeQuery,
  PromoSettingByUsageTypeDocument,
  FilterPageQuery,
  FilterPageDocument
} from '~/config/generated/graphql';
import {
  CarBrandAndCategoryData,
  PromoSettingData,
  FilterPageData
} from '~/App/shared/interfaces/store/CmsData';
import RecentlyViewedAuctions from '~/App/shared/components/RecentlyViewedAuctions';
import { isOnServer } from '~/helpers/isOnServer';
import HeadInjection from '~/App/views/CarTreeAndCategory/components/HeadInjection';
import Section from '~/App/shared/components/Layout/Section';
import TrustPilot from '~/App/shared/components/TrustPilot';
import HeadingAndPreambleSection from '~/App/views/CarTreeAndCategory/components/HeadingAndPreambleSection';
import ObjectsList from '~/App/views/CarTreeAndCategory/components/ObjectsList';
import CarInformationSection from '~/App/views/CarTreeAndCategory/components/CarInformationSection';
import AllFamilyNavigationSection from '~/App/views/CarTreeAndCategory/components/AllFamilyNavigationSection';
import AllBrandsSection from '~/App/views/CarTreeAndCategory/components/AllBrandsSection';
import { MainCategory } from '~/App/shared/types/MainCategoryTypes';
import { isMainCategory } from '~/helpers/filterSearchParams';
import { DataHookFunctionProps } from '~/helpers/provideDataHook';
import { getCmsData } from '~/helpers/cmsDatoUtils';
import {
  fetchFacilities,
  fetchStoreObjects
} from '~/App/shared/actions/storeObjectActions';
import { deserializationFilters } from '~/helpers/storeObjects';
import { localizationSelector } from '~/App/shared/selectors/localization';
import { RouteComponentProps } from '~/App/shared/interfaces/routes';

type DataHookFetchedData = {
  cmsData: CarBrandAndCategoryData | undefined;
  promoData: PromoSettingData | undefined;
  mainCategory: MainCategory;
  searchQuery: string;
};
type DataHook = Partial<DataHookFetchedData> & {
  ssrDataHookProcessed?: boolean;
};
type ComponentProps = RouteComponentProps;

export type Props = ComponentProps & DataHook;

export const CarBrandAndCategory = ({
  staticContext,
  cmsData,
  promoData,
  ssrDataHookProcessed,
  mainCategory,
  searchQuery
}: Props) => {
  const isServerSidePrepassRender =
    isOnServer &&
    typeof ssrDataHookProcessed === 'boolean' &&
    !ssrDataHookProcessed;

  if (!isServerSidePrepassRender && (!cmsData || cmsData?.notFound)) {
    return <NotFound staticContext={staticContext} />;
  }

  return (
    <>
      {cmsData && <HeadInjection cmsData={cmsData} />}
      <Section spacingTop={0.75} spacingBottom={0.75}>
        <TrustPilot type="Horizontal" />
      </Section>
      <HeadingAndPreambleSection
        headingText={cmsData?.headingText ?? ''}
        headingTag={cmsData?.headingTag}
        logo={cmsData?.logo}
        preamble={cmsData?.preamble}
      />
      {searchQuery && (
        <ObjectsList
          queryString={searchQuery}
          promoSettingsData={promoData}
          mainCategory={mainCategory}
        />
      )}
      <CarInformationSection text={cmsData?.text} seoInfo={cmsData?.seoInfo} />
      {cmsData?.allFamily && cmsData?.allFamily?.length > 0 && (
        <AllFamilyNavigationSection
          brandName={cmsData?.name ?? ''}
          brandSlug={cmsData?.slug ?? ''}
          allFamily={cmsData?.allFamily}
        />
      )}
      <Section
        maxWidth={64}
        spacingTop={1.5}
        spacingBottom={3.5}
        backgroundColor="light"
      >
        <RecentlyViewedAuctions />
      </Section>
      {mainCategory && <AllBrandsSection mainCategory={mainCategory} />}
    </>
  );
};

export const getDataHook =
  ({ mainCategory }: { mainCategory: MainCategory }) =>
  async ({
    match: { params },
    dispatch,
    getState
  }: DataHookFunctionProps<{ carBrandOrCategorySlug: string }> &
    ComponentProps): Promise<DataHookFetchedData> => {
    let cmsData: DataHookFetchedData['cmsData'];
    let promoData: DataHookFetchedData['promoData'];
    let searchQuery = '';

    const { currentLocale } = localizationSelector(getState());

    const carBrandOrCategorySlug = decodeURIComponent(
      params.carBrandOrCategorySlug
    );

    const filterPageCmsData = await getCmsData<FilterPageQuery, FilterPageData>(
      {
        dispatch,
        getState,
        storeKey: 'filterPage',
        storeNestedKey: mainCategory,
        query: FilterPageDocument,
        variables: { mainCategory, locale: currentLocale }
      }
    );

    if (filterPageCmsData?.id) {
      cmsData = await getCmsData<
        CarBrandAndCategoryBySlugQuery,
        CarBrandAndCategoryData
      >({
        dispatch,
        getState,
        storeKey: 'carBrandAndCategory',
        storeNestedKey: `${mainCategory}.${carBrandOrCategorySlug}`,
        query: CarBrandAndCategoryBySlugDocument,
        variables: {
          slug: carBrandOrCategorySlug,
          filterPage: filterPageCmsData?.id,
          locale: currentLocale
        }
      });

      const searchParams = new URLSearchParams(cmsData?.queryParams ?? '');
      if (isMainCategory(mainCategory)) {
        searchParams.append('vehicleType', mainCategory);
      }
      if (!searchParams.has('orderBy')) {
        searchParams.append('orderBy', '-grade');
      }
      searchQuery = searchParams.toString();

      const responses = await Promise.all([
        getCmsData<PromoSettingByUsageTypeQuery, PromoSettingData>({
          dispatch,
          getState,
          storeKey: 'promoSettings',
          storeNestedKey: 'promoSettings.car_brand_and_category_page',
          query: PromoSettingByUsageTypeDocument,
          variables: {
            usageType: 'car_brand_and_category_page',
            locale: currentLocale
          }
        }),
        dispatch(
          fetchStoreObjects({
            filters: deserializationFilters(searchQuery)
          })
        ),
        dispatch(fetchFacilities())
      ]);

      promoData = responses[0];
    }

    return { cmsData, promoData, mainCategory, searchQuery };
  };
